// src/pages/Case/CasesPage.jsx

import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Button,
  Badge,
  OverlayTrigger,
  Tooltip,
  Dropdown,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  FolderKanban,
  FilePlus2,
  Pencil,
  Trash2,
  Filter,
  CheckCircle,
  XCircle,
  UserCircle2,
  Gavel,
  CalendarClock,
  Activity,
  ClipboardList,
  ScrollText,
} from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import CaseReportPDF from "./CaseReportPDF";
import PaginatedTable from "../Common/PaginatedTable";
import Unauthorized from "../../components/Common/Unauthorized";
import usePermission from "../../hooks/usePermission";
import EntityDetailsSidebar from "../../components/Common/EntityDetailsSidebar";
import { formatDateTime } from "../../utils/formatters";
import {
  getCasesPaged,
  getCaseById,
  updateCase,
  deleteCase,
  getMyCasesPaged,
  getCaseReport,
} from "../../services/caseService";

// Modales de detalle
import CustomerDetailsModal from "../Customer/CustomerDetailsModal";
import { getCustomerById } from "../../services/customerService";

import LawyerDetailsModal from "../Lawyer/LawyerDetailsModal";
import { getLawyerById } from "../../services/lawyerService";

// -------------------------------------------------------------
//  Helpers de normalización y mapeo nombre → código
// -------------------------------------------------------------
const normalize = (s) =>
  (s || "")
    .toString()
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const STATUS_NAME_TO_CODE = {
  // ------------------------
  // Estados básicos
  // ------------------------
  REGISTRADO: "REGISTRADO",

  "EN REVISION": "EN_REVISION",
  "EN REVISION / EN ANALISIS": "EN_REVISION",

  "SOLICITA INFORMACION ADICIONAL": "SOL_INFO",

  ADMITIDO: "ADMITIDO",

  "EN TRAMITE": "EN_TRAMITE",
  "EN TRAMITE INTERNO": "EN_TRAMITE_INTERNO",

  "EN NEGOCIACION": "EN_NEGOCIACION",

  "EN ELABORACION DE RESPUESTA": "ELAB_RESPUESTA",

  SUSPENDIDO: "SUSPENDIDO",

  "EN APELACION": "EN_APELACION",

  "RESPONDIDO / CONTESTADO": "RESPONDIDO",
  RESPONDIDO: "RESPONDIDO",

  // ------------------------
  // Finalización
  // ------------------------
  "FINALIZADO (CON FALLO)": "FINALIZADO_FALLO",
  "FINALIZADO (POR ACUERDO)": "FINALIZADO_ACUERDO",

  "CERRADO / ARCHIVADO": "CERRADO",
  CERRADO: "CERRADO",

  "ESCALADO / RECURSO / TUTELA": "ESCALADO",
  ESCALADO: "ESCALADO",

  CANCELADO: "CANCELADO",
  VENCIDO: "VENCIDO",

  "OTRO ESTADO": "OTRO",
  OTRO: "OTRO",
};

const PRIORITY_NAME_TO_CODE = {
  URGENTE: "URGENTE",
  ALTA: "ALTA",
  MEDIA: "MEDIA",
  BAJA: "BAJA",
  OTRO: "OTRO",
};

const getStatusCode = (row) => {
  const direct = (row?.caseStatusCode || row?.statusCode || "")
    .toString()
    .toUpperCase();
  if (direct) return direct;
  const name = normalize(row?.caseStatusName || row?.statusName);
  return STATUS_NAME_TO_CODE[name] || "";
};

const getPriorityCode = (row) => {
  const direct = (row?.priorityCode || "").toString().toUpperCase();
  if (direct) return direct;
  const name = normalize(row?.priorityName);
  return PRIORITY_NAME_TO_CODE[name] || "";
};

// -------------------------------------------------------------
// Filtros visibles (primarios y secundarios)
// -------------------------------------------------------------

// Filtros principales (los más usados, como “chips”)
const PRIMARY_STATUS_FILTERS = [
  ["ALL", "Todos"],
  ["REGISTRADO", "Registrado"],
  ["EN_TRAMITE", "En trámite"],
  ["FINALIZADO", "Finalizados"], // combina FINALIZADO_FALLO + FINALIZADO_ACUERDO
  ["VENCIDO", "Vencido"],
];

// Filtros secundarios en dropdown “Más estados”
const SECONDARY_STATUS_FILTERS = [
  ["EN_REVISION", "En revisión / análisis"],
  ["SOL_INFO", "Solicita información adicional"],
  ["EN_TRAMITE_INTERNO", "En trámite interno"],
  ["EN_NEGOCIACION", "En negociación"],
  ["ELAB_RESPUESTA", "Elab. respuesta"],
  ["SUSPENDIDO", "Suspendido"],
  ["EN_APELACION", "En apelación"],
  ["RESPONDIDO", "Respondido"],
  ["CERRADO", "Cerrado / Archivado"],
  ["ESCALADO", "Escalado / Recurso / Tutela"],
  ["CANCELADO", "Cancelado"],
  ["OTRO", "Otro estado"],
];

const PRIORITY_FILTERS = [
  ["ALL", "Todas"],
  ["URGENTE", "Urgente"],
  ["ALTA", "Alta"],
  ["MEDIA", "Media"],
  ["BAJA", "Baja"],
];

// -------------------------------------------------------------
// Badges visuales
// -------------------------------------------------------------
const statusBadge = (statusCode, statusName) => {
  const code = (statusCode || "").toUpperCase();

  switch (code) {
    // ---- Estados iniciales / neutrales ----
    case "REGISTRADO":
      return <Badge bg="secondary">{statusName || "Registrado"}</Badge>;

    case "EN_REVISION":
      return <Badge bg="info">{statusName || "En revisión"}</Badge>;

    case "SOL_INFO":
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>Solicita información adicional</Tooltip>}
        >
          <Badge bg="warning" text="dark">
            {statusName || "Solicita información"}
          </Badge>
        </OverlayTrigger>
      );

    // ---- Estados del proceso ----
    case "ADMITIDO":
      return <Badge bg="primary">{statusName || "Admitido"}</Badge>;

    case "EN_TRAMITE":
    case "EN_TRAMITE_INTERNO":
      return <Badge bg="primary">{statusName || "En trámite"}</Badge>;

    case "EN_NEGOCIACION":
      return (
        <Badge bg="warning" text="dark">
          {statusName || "En negociación"}
        </Badge>
      );

    case "ELAB_RESPUESTA":
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>En elaboración de respuesta</Tooltip>}
        >
          <Badge bg="info" text="dark">
            {statusName || "Elab. respuesta"}
          </Badge>
        </OverlayTrigger>
      );

    case "SUSPENDIDO":
      return <Badge bg="dark">{statusName || "Suspendido"}</Badge>;

    case "EN_APELACION":
      return <Badge bg="info">{statusName || "En apelación"}</Badge>;

    case "RESPONDIDO":
      return <Badge bg="success">{statusName || "Respondido"}</Badge>;

    // ---- Finalización ----
    case "FINALIZADO_FALLO":
    case "FINALIZADO_ACUERDO":
      return <Badge bg="success">{statusName || "Finalizado"}</Badge>;

    case "CERRADO":
      return (
        <Badge bg="secondary" text="dark">
          {statusName || "Cerrado"}
        </Badge>
      );

    case "ESCALADO":
      return (
        <Badge bg="danger">{statusName || "Escalado / Recurso / Tutela"}</Badge>
      );

    case "CANCELADO":
      return <Badge bg="danger">{statusName || "Cancelado"}</Badge>;

    // ---- Expirado / crítico ----
    case "VENCIDO":
      return <Badge bg="danger">{statusName || "Vencido"}</Badge>;

    // ---- Otros ----
    case "OTRO":
      return (
        <Badge bg="light" text="dark">
          {statusName || "Otro"}
        </Badge>
      );

    // ---- Fallback ----
    default:
      return (
        <Badge bg="light" text="dark">
          {statusName || "—"}
        </Badge>
      );
  }
};

const priorityBadge = (priorityCode, priorityName) => {
  const code = (priorityCode || "").toUpperCase();
  switch (code) {
    case "URGENTE":
      return (
        <Badge bg="danger" className="d-inline-flex align-items-center gap-1">
          <CalendarClock size={14} /> {priorityName || "Urgente"}
        </Badge>
      );
    case "ALTA":
      return <Badge bg="warning">{priorityName || "Alta"}</Badge>;
    case "MEDIA":
      return <Badge bg="info">{priorityName || "Media"}</Badge>;
    case "BAJA":
      return <Badge bg="success">{priorityName || "Baja"}</Badge>;
    default:
      return <Badge bg="secondary">{priorityName || "—"}</Badge>;
  }
};

// Badge de vencimiento (al día / alerta / vencido / sin vencimiento)
const deadlineBadge = (row) => {
  const isDeadline = row.isDeadlineBasedCase;
  const isExpired = row.isExpired;
  const isAlert = row.isAlertTriggered;
  const deadlineLabel = row.responseDeadline
    ? formatDateTime(row.responseDeadline)
    : "—";

  if (!isDeadline) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>Este caso no maneja fecha límite</Tooltip>}
      >
        <Badge bg="light" text="dark">
          Sin vencimiento
        </Badge>
      </OverlayTrigger>
    );
  }

  if (isExpired) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>Caso vencido. Fecha límite: {deadlineLabel}</Tooltip>}
      >
        <Badge bg="danger" className="d-inline-flex align-items-center gap-1">
          <CalendarClock size={14} />
          Vencido
        </Badge>
      </OverlayTrigger>
    );
  }

  if (isAlert) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>Próximo a vencer. Fecha límite: {deadlineLabel}</Tooltip>
        }
      >
        <Badge
          bg="warning"
          text="dark"
          className="d-inline-flex align-items-center gap-1"
        >
          <CalendarClock size={14} />
          Próx. a vencer
        </Badge>
      </OverlayTrigger>
    );
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>
          Con fecha límite. Fecha de vencimiento: {deadlineLabel}
        </Tooltip>
      }
    >
      <Badge bg="success" className="d-inline-flex align-items-center gap-1">
        <CalendarClock size={14} />
        Al día
      </Badge>
    </OverlayTrigger>
  );
};

// -------------------------------------------------------------
// Estilos condicionales de fila (vencido / próximo a vencer)
// -------------------------------------------------------------
const conditionalRowStyles = [
  {
    when: (row) => row.isDeadlineBasedCase && row.isExpired,
    style: {
      backgroundColor: "#fff5f5",
      borderLeft: "4px solid #dc3545",
    },
  },
  {
    when: (row) =>
      row.isDeadlineBasedCase && !row.isExpired && row.isAlertTriggered,
    style: {
      backgroundColor: "#fff8e1",
      borderLeft: "4px solid #ffc107",
    },
  },
];

// -------------------------------------------------------------
// Componente principal
// -------------------------------------------------------------

const CasesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Modo "mis casos" para el abogado: ruta /dashboard/my-cases
  const isMyCasesView = useMemo(
    () => location.pathname.startsWith("/dashboard/my-cases"),
    [location.pathname]
  );

  const hasPermission = usePermission();

  // Detectar en qué ruta estoy
  const isMyCasesPage = location.pathname.includes("/dashboard/my-cases");
  const isAllCasesPage = location.pathname.includes("/dashboard/cases");

  // Permisos
  const canViewMyCases = hasPermission("casos.view");
  const canViewAllCases = hasPermission("casos.view.all");

  // Regla de negocio:
  // - /my-cases => requiere casos.view
  // - /cases    => requiere casos.view.all
  const canView = isMyCasesPage ? canViewMyCases : canViewAllCases;

  const canCreate = hasPermission("casos.create");
  const canUpdate = hasPermission("casos.update");
  const canDelete = hasPermission("casos.delete");

  // Estado tabla
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Búsqueda con debounce
  const [searchTerm, setSearchTerm] = useState("");
  const searchRef = useRef(null);
  const [appliedSearch, setAppliedSearch] = useState("");

  // Filtros rápidos
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [priorityFilter, setPriorityFilter] = useState("ALL");

  // Sidebar detalle caso
  const [selected, setSelected] = useState(null);

  // Modal detalle cliente
  const [showCustomer, setShowCustomer] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);

  // Modal detalle abogado
  const [showLawyer, setShowLawyer] = useState(false);
  const [lawyerId, setLawyerId] = useState(null);
  const [lawyerDetail, setLawyerDetail] = useState(null);
  const [lawyerLoading, setLawyerLoading] = useState(false);

  // Abre modal de cliente y carga su detalle
  const openCustomerModal = useCallback(async (id) => {
    if (!id) return;
    setCustomerId(id);
    setShowCustomer(true);
    setCustomerDetail(null);
    setCustomerLoading(true);
    try {
      const r = await getCustomerById(id);
      if (!r?.success) throw new Error();
      setCustomerDetail(r.data);
    } catch {
      Swal.fire("Error", "No se pudo cargar", "error");
      setShowCustomer(false);
    } finally {
      setCustomerLoading(false);
    }
  }, []);

  // Abre modal de abogado y carga su detalle
  const openLawyerModal = useCallback(async (id) => {
    if (!id) return;
    setLawyerId(id);
    setShowLawyer(true);
    setLawyerDetail(null);
    setLawyerLoading(true);
    try {
      const r = await getLawyerById(id);
      if (!r?.success) throw new Error();
      setLawyerDetail(r.data);
    } catch {
      Swal.fire("Error", "No se pudo cargar los datos del  abogado.", "error");
      setShowLawyer(false);
    } finally {
      setLawyerLoading(false);
    }
  }, []);

  // Reload del detalle del cliente (desde el propio modal)
  const reloadCustomer = useCallback(async () => {
    if (!customerId) return;
    const r = await getCustomerById(customerId);
    if (r?.success) setCustomerDetail(r.data);
  }, [customerId]);

  // Filtros locales (estado + prioridad)
  const applyQuickFilters = useCallback(
    (items) =>
      items.filter((it) => {
        const s = getStatusCode(it);
        const p = getPriorityCode(it);

        const statusOk =
          statusFilter === "ALL" ||
          (statusFilter === "FINALIZADO" &&
            ["FINALIZADO_FALLO", "FINALIZADO_ACUERDO"].includes(s)) ||
          s === statusFilter;

        const priorityOk = priorityFilter === "ALL" || p === priorityFilter;

        return statusOk && priorityOk;
      }),
    [statusFilter, priorityFilter]
  );

  // Carga de datos
  const fetchCases = useCallback(
    async (page, size, search) => {
      try {
        setLoading(true);
        // Si estamos en la vista de abogado ("Mis casos"), llamamos al endpoint filtrado.
        // Si no, usamos el listado general (admin / company admin).
        const res = isMyCasesView
          ? await getMyCasesPaged(page, size, search)
          : await getCasesPaged(page, size, search);
        if (!res?.success) throw new Error(res?.message);
        const baseItems = res.data.items || [];
        const filtered = applyQuickFilters(baseItems);
        setRows(filtered);
        setTotalRows(res.data.totalCount || 0);
      } catch {
        Swal.fire("Error", "No se pudieron cargar los casos", "error");
      } finally {
        setLoading(false);
      }
    },
    [applyQuickFilters, isMyCasesView]
  );

  // Debounce search
  useEffect(() => {
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {
      setAppliedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(searchRef.current);
  }, [searchTerm]);

  useEffect(() => {
    fetchCases(currentPage, perPage, appliedSearch);
  }, [
    fetchCases,
    currentPage,
    perPage,
    appliedSearch,
    statusFilter,
    priorityFilter,
  ]);

  // Acciones de navegación
  const goCreate = useCallback(
    () =>
      navigate("/dashboard/cases/create", {
        state: {
          from: isMyCasesPage ? "/dashboard/my-cases" : "/dashboard/cases",
        },
      }),
    [navigate, isMyCasesPage]
  );
  const goEdit = useCallback(
    (id) =>
      navigate(`/dashboard/cases/edit/${id}`, {
        state: {
          from: isMyCasesPage ? "/dashboard/my-cases" : "/dashboard/cases",
        },
      }),
    [navigate, isMyCasesPage]
  );
  const goTimeline = useCallback(
    (id) =>
      navigate(`/dashboard/cases/${id}/timeline`, {
        state: {
          from: isMyCasesPage ? "/dashboard/my-cases" : "/dashboard/cases",
        },
      }),
    [navigate, isMyCasesPage]
  );
  const goManage = useCallback(
    (id) =>
      navigate(`/dashboard/cases/${id}/manage`, {
        state: {
          from: isMyCasesPage ? "/dashboard/my-cases" : "/dashboard/cases",
        },
      }),
    [navigate, isMyCasesPage]
  );

  const onView = useCallback(async (id) => {
    try {
      const r = await getCaseById(id);
      if (!r?.success) throw new Error();
      setSelected(r.data);
    } catch {
      Swal.fire("Error", "No se pudo cargar el detalle del caso.", "error");
    }
  }, []);

  const onDelete = useCallback(
    async (id) => {
      const ok = await Swal.fire({
        title: "¿Eliminar caso?",
        text: "Esta acción realiza borrado lógico.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!ok.isConfirmed) return;

      try {
        const res = await deleteCase(id);
        if (!res?.success) throw new Error(res?.message);
        Swal.fire("Eliminado", "El caso fue eliminado.", "success");
        fetchCases(currentPage, perPage, appliedSearch);
        setSelected(null);
      } catch {
        Swal.fire("Error", "No se pudo eliminar el caso.", "error");
      }
    },
    [appliedSearch, currentPage, perPage, fetchCases]
  );

  const onToggleActive = useCallback(
    async (row) => {
      const next = !row.isActive;
      const ask = await Swal.fire({
        title: next ? "¿Activar caso?" : "¿Inactivar caso?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, confirmar",
        cancelButtonText: "Cancelar",
      });
      if (!ask.isConfirmed) return;

      try {
        const r = await getCaseById(row.id);
        if (!r?.success) throw new Error();
        const d = r.data;

        const payload = {
          id: d.id,
          title: d.title,
          description: d.description,
          caseNumber: d.caseNumber,
          customerId: d.customerId,
          lawyerResponsibleId: d.lawyerResponsibleId,
          caseTypeId: d.caseTypeId,
          caseStatusId: d.caseStatusId,
          jurisdictionId: d.jurisdictionId,
          courtId: d.courtId,
          priorityId: d.priorityId,
          externalCaseNumber: d.externalCaseNumber,
          filingDate: d.filingDate,
          contactEmail: d.contactEmail,
          notes: d.notes,
          responseDeadline: d.responseDeadline,
          alertDaysBefore: d.alertDaysBefore,
          isDeadlineBasedCase: d.isDeadlineBasedCase,
          isActive: next,
        };

        const res = await updateCase(row.id, payload);
        if (!res?.success) throw new Error();
        Swal.fire("Éxito", "Estado actualizado.", "success");
        fetchCases(currentPage, perPage, appliedSearch);
      } catch {
        Swal.fire("Error", "No se pudo actualizar el estado.", "error");
      }
    },
    [appliedSearch, currentPage, perPage, fetchCases]
  );
  const onGenerateReport = useCallback(async (id) => {
    try {
      const swalRef = Swal.fire({
        title: "Generando informe...",
        text: "Por favor espera mientras preparamos el PDF.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res = await getCaseReport(id);
      if (!res?.success) {
        throw new Error(
          res?.message || "No se pudo obtener la data del informe."
        );
      }

      const reportData = res.data;

      // Generar el PDF en el cliente usando CaseReportPDF
      const blob = await pdf(<CaseReportPDF data={reportData} />).toBlob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `informe-caso-${reportData.internalCode || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      Swal.close(swalRef);
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        "No se pudo generar el informe del caso. Intenta nuevamente.",
        "error"
      );
    }
  }, []);
  // Bloque de texto largo con expandir/contraer (para descripción en sidebar)
  const DescriptionBlock = ({ text, lines = 5 }) => {
    const [expanded, setExpanded] = React.useState(false);
    if (!text) return <span>—</span>;

    return (
      <div>
        <div
          style={{
            whiteSpace: "pre-wrap",
            lineHeight: "1.4",
            padding: "0.5rem 0.75rem",
            background: "#f8f9fa",
            border: "1px solid #e9ecef",
            borderRadius: 8,
            maxHeight: expanded ? "none" : `${lines * 1.4}em`,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: expanded ? "unset" : lines,
          }}
        >
          {text}
        </div>

        <div className="mt-1 d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? "Ver menos" : "Ver más"}
          </button>
          {!expanded && (
            <small className="text-muted">({lines} líneas visibles)</small>
          )}
        </div>
      </div>
    );
  };

  // Resumen de casos (vencidos, próximos a vencer, urgentes)
  const stats = useMemo(() => {
    let expired = 0;
    let warning = 0;
    let urgent = 0;

    rows.forEach((r) => {
      if (r.isDeadlineBasedCase && r.isExpired) expired++;
      else if (r.isDeadlineBasedCase && r.isAlertTriggered) warning++;

      if (getPriorityCode(r) === "URGENTE") urgent++;
    });

    return { expired, warning, urgent };
  }, [rows]);

  // Esquema para el panel lateral de detalles
  const detailSchema = useMemo(
    () => [
      { label: "Código interno", key: "internalCode" },
      { label: "Título", key: "title" },
      {
        label: "Descripción",
        key: "description",
        render: (v) => <DescriptionBlock text={v} lines={6} />,
      },
      { label: "Cliente", key: "customerName" },
      { label: "Abogado responsable", key: "lawyerResponsibleName" },
      { label: "Tipo de caso", key: "caseTypeName" },
      { label: "Estado", key: "caseStatusName" },
      { label: "Prioridad", key: "priorityName" },
      { label: "N° externo", key: "externalCaseNumber" },
      {
        label: "Radicación",
        key: "filingDate",
        render: (v) => (v ? formatDateTime(v) : "—"),
      },
      {
        label: "¿Maneja vencimiento?",
        key: "isDeadlineBasedCase",
        render: (v) => (v ? "Sí" : "No"),
      },
      {
        label: "Fecha límite de respuesta",
        key: "responseDeadline",
        render: (v) => (v ? formatDateTime(v) : "—"),
      },
      {
        label: "Alerta disparada",
        key: "isAlertTriggered",
        render: (v) => (v ? "Sí" : "No"),
      },
      {
        label: "Días de alerta antes de vencimiento",
        key: "alertDaysBefore",
      },
      { label: "Correo de contacto", key: "contactEmail" },
      { label: "Notas", key: "notes" },
      { label: "Creado", key: "createdAt", render: formatDateTime },
      { label: "Actualizado", key: "updatedAt", render: formatDateTime },
    ],
    []
  );

  // Definición de columnas de la tabla
  const columns = useMemo(
    () => [
      {
        name: "Caso",
        wrap: true,
        sortable: true,
        selector: (r) => r.internalCode,
        cell: (r) => (
          <div className="d-flex flex-column">
            <span className="fw-semibold">
              {r.internalCode || r.caseNumber || "—"}
            </span>
            <small className="text-muted">{r.title || "—"}</small>
          </div>
        ),
        width: "180px",
      },

      {
        name: "Cliente",
        wrap: true,
        sortable: true,
        selector: (r) => r.customerName,
        cell: (r) => (
          <div className="d-flex align-items-center gap-2">
            <UserCircle2 size={16} className="text-muted" />
            <Button
              variant="link"
              className="p-0 text-decoration-none"
              onClick={(e) => {
                e.stopPropagation();
                openCustomerModal(r.customerId);
              }}
              title="Ver cliente"
            >
              {r.customerName || "—"}
            </Button>
          </div>
        ),
        width: "300px",
      },

      // Columna condicional
      ...(canViewAllCases
        ? [
            {
              name: "Abogado",
              wrap: true,
              sortable: true,
              selector: (r) => r.lawyerResponsibleName,
              cell: (r) => (
                <div className="d-flex align-items-center gap-2">
                  <Gavel size={16} className="text-muted" />
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      openLawyerModal(r.lawyerResponsibleId);
                    }}
                    title="Ver abogado"
                  >
                    {r.lawyerResponsibleName || "—"}
                  </Button>
                </div>
              ),
              width: "200px",
            },
          ]
        : []),

      {
        name: "Estado",
        center: true,
        width: "160px",
        cell: (r) =>
          statusBadge(getStatusCode(r), r.caseStatusName || r.statusName),
      },

      {
        name: "Prioridad",
        center: true,
        width: "120px",
        cell: (r) => priorityBadge(getPriorityCode(r), r.priorityName),
      },

      {
        name: "Vencimiento",
        center: true,
        width: "150px",
        cell: (r) => deadlineBadge(r),
      },

      {
        name: "Activo",
        center: true,
        width: "140px",
        cell: (row) =>
          canUpdate ? (
            <Button
              variant="light"
              size="sm"
              className="d-flex align-items-center gap-1 border-0"
              onClick={(e) => {
                e.stopPropagation();
                onToggleActive(row);
              }}
            >
              {row.isActive ? (
                <>
                  <CheckCircle size={16} className="text-success" /> Activo
                </>
              ) : (
                <>
                  <XCircle size={16} className="text-danger" /> Inactivo
                </>
              )}
            </Button>
          ) : row.isActive ? (
            <span className="text-success fw-semibold">Activo</span>
          ) : (
            <span className="text-danger fw-semibold">Inactivo</span>
          ),
      },

      {
        name: "Acciones",
        center: true,
        width: "230px",
        cell: (row) => (
          <div className="d-flex gap-2">
            {/* Gestionar caso */}
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>Gestionar caso (notas, adjuntos, seguimiento)</Tooltip>
              }
            >
              <Button
                variant="success"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  goManage(row.id);
                }}
              >
                <ClipboardList size={16} />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Descargar informe del caso (PDF)</Tooltip>}
            >
              <Button
                variant="outline-success"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onGenerateReport(row.id);
                }}
              >
                <ScrollText size={16} />
              </Button>
            </OverlayTrigger>

            {canUpdate && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Editar caso</Tooltip>}
              >
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    goEdit(row.id);
                  }}
                >
                  <Pencil size={16} />
                </Button>
              </OverlayTrigger>
            )}

            {/* Timeline */}
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Ver historial / timeline</Tooltip>}
            >
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  goTimeline(row.id);
                }}
              >
                <Activity size={16} />
              </Button>
            </OverlayTrigger>

            {canDelete && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Eliminar caso</Tooltip>}
              >
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(row.id);
                  }}
                >
                  <Trash2 size={16} />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [
      canViewAllCases,
      canUpdate,
      canDelete,
      goEdit,
      goTimeline,
      goManage,
      onDelete,
      onToggleActive,
      openCustomerModal,
      openLawyerModal,
    ]
  );

  if (!canView) return <Unauthorized />;

  // Estado vacío
  const EmptyState = () => (
    <div className="text-center py-5">
      <FolderKanban size={42} className="text-muted mb-2" />
      <h6 className="text-muted">No hay casos que coincidan</h6>
      <p className="text-muted small mb-3">
        Ajusta los filtros o cambia el texto de búsqueda.
      </p>
      {canCreate && (
        <Button onClick={goCreate}>
          <FilePlus2 size={16} className="me-1" />
          Crear el primer caso
        </Button>
      )}
    </div>
  );

  // -------------------------------------------------------------
  // Render principal
  // -------------------------------------------------------------
  return (
    <div className="container-fluid mt-2 px-4 w-100">
      <Card className="p-3">
        {/* Header con filtros rápidos + resumen */}
        <div
          className="position-sticky top-0 bg-white py-3 mb-3"
          style={{ zIndex: 2 }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div className="d-flex flex-column gap-1">
              <div className="d-flex align-items-center gap-2">
                <FolderKanban className="text-dark" size={22} />
                <h4 className="mb-0 text-dark">Casos</h4>
                <Badge bg="light" text="dark" className="ms-2">
                  {totalRows} en total
                </Badge>
              </div>

              {/* Resumen de estado operacional */}
              <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                <Badge bg="danger">
                  Vencidos: <strong>{stats.expired}</strong>
                </Badge>
                <Badge bg="warning" text="dark">
                  Próx. a vencer: <strong>{stats.warning}</strong>
                </Badge>
                <Badge bg="info">
                  Urgentes: <strong>{stats.urgent}</strong>
                </Badge>
              </div>
            </div>

            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2">
              {/* Filtros rápidos: Estado */}
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Filtrar por estado</Tooltip>}
              >
                <div className="d-flex flex-column gap-1">
                  <div className="d-flex align-items-center gap-2">
                    <Filter size={16} className="text-muted" />
                    <div className="d-flex gap-1 flex-wrap">
                      {PRIMARY_STATUS_FILTERS.map(([code, label]) => (
                        <Button
                          key={code}
                          size="sm"
                          variant={
                            statusFilter === code
                              ? "primary"
                              : "outline-secondary"
                          }
                          onClick={() => {
                            setStatusFilter(code);
                            setCurrentPage(1);
                          }}
                        >
                          {label}
                        </Button>
                      ))}

                      {/* Dropdown para estados secundarios */}
                      <Dropdown>
                        <Dropdown.Toggle
                          size="sm"
                          variant={
                            SECONDARY_STATUS_FILTERS.some(
                              ([code]) => code === statusFilter
                            )
                              ? "primary"
                              : "outline-secondary"
                          }
                        >
                          Más estados
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {SECONDARY_STATUS_FILTERS.map(([code, label]) => (
                            <Dropdown.Item
                              key={code}
                              active={statusFilter === code}
                              onClick={() => {
                                setStatusFilter(code);
                                setCurrentPage(1);
                              }}
                            >
                              {label}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>

                  {/* Filtros rápidos: Prioridad */}
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Filtrar por prioridad</Tooltip>}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <Filter size={16} className="text-muted" />
                      <div className="d-flex gap-1 flex-wrap">
                        {PRIORITY_FILTERS.map(([code, label]) => (
                          <Button
                            key={code}
                            size="sm"
                            variant={
                              priorityFilter === code
                                ? "primary"
                                : "outline-secondary"
                            }
                            onClick={() => {
                              setPriorityFilter(code);
                              setCurrentPage(1);
                            }}
                          >
                            {label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </OverlayTrigger>
                </div>
              </OverlayTrigger>

              {/* CTA crear */}
              {canCreate && (
                <Button onClick={goCreate} className="ms-md-2">
                  <FilePlus2 size={16} className="me-1" />
                  Nuevo caso
                </Button>
              )}
            </div>
          </div>
        </div>

        <PaginatedTable
          keyField="id"
          columns={columns}
          data={rows}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={(p) => setCurrentPage(p)}
          onRowsPerPageChange={(n, p) => {
            setPerPage(n);
            setCurrentPage(p);
          }}
          onSearch={(t) => {
            setSearchTerm(t);
            setCurrentPage(1);
          }}
          progressPending={loading}
          onRowClicked={(r) => onView(r.id)}
          noDataComponent={<EmptyState />}
          conditionalRowStyles={conditionalRowStyles}
        />
      </Card>

      {/* Panel lateral de detalle del caso */}
      <EntityDetailsSidebar
        isVisible={!!selected}
        onClose={() => setSelected(null)}
        title="Detalle del caso"
        icon={<FolderKanban size={20} />}
        data={selected}
        schema={detailSchema}
      />

      {/* Modal de detalles del cliente */}
      <CustomerDetailsModal
        visible={showCustomer}
        onClose={() => setShowCustomer(false)}
        data={customerDetail}
        loading={customerLoading}
        onReload={reloadCustomer}
      />

      {/* Modal de detalles del abogado */}
      <LawyerDetailsModal
        visible={showLawyer}
        onClose={() => setShowLawyer(false)}
        data={lawyerDetail}
        loading={lawyerLoading}
      />
    </div>
  );
};

export default CasesPage;
